import React, { memo, useEffect, useState } from "react";
import {
  Accordion,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import CurrencyInput from "react-currency-input";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import ptBR from "date-fns/locale/pt-BR";

import { Creators as NotificationsActions } from "./../../store/ducks/notifications";

import { isScopes } from "./../../services/Auth";
import { maskCPF } from "./../../utils/CPF";
import { maskCNPJ } from "./../../utils/CNPJ";
import { jsonToForm, formToJson } from "./../../utils/TrafficTicket";
import { isEmpty, ucFirstAll } from "./../../utils/String";
import { isValidForm } from "./../../utils/Form";
import { clearObject, mergeObject } from "./../../utils/Object";
import DateUtil from "./../../utils/Date";

import ClientService from "./../../services/Client";
import ClientSchemaReduced from "./../../services/Client/SchemaReduced";
import DriverService from "./../../services/Driver";
import DriverSchemaReduced from "./../../services/Driver/SchemaReduced";
import PatrimonyService from "./../../services/Patrimony";
import PatrimonySchemaReduced from "./../../services/Patrimony/SchemaReduced";
import TrafficTicketService from "./../../services/TrafficTicket";
import TrafficTicketSchema from "./../../services/TrafficTicket/Schema";

const ACTIVE_KEY_DETAIL_DATA = "1";
const ACTIVE_KEY_GENERAL_DATA = "0";

registerLocale("pt-BR", ptBR);

const TrafficTicket = ({
  options,
  props: { currentUser },
  funcs: { addNotice }
}) => {
  const { t } = useTranslation();
  const clientService = new ClientService();
  const driverService = new DriverService();
  const patrimonyService = new PatrimonyService();
  const trafficTicketService = new TrafficTicketService();

  const {
    trafficTicket: __trafficTicket = {},
    close: __close = () => {}
  } = options;

  const [trafficTicket, setTrafficTicket] = useState(jsonToForm(mergeObject(JSON.parse(JSON.stringify(TrafficTicketSchema)), __trafficTicket)));

  useEffect(() => {
    setTimeout(() => {
      if(!isScopes({ currentUser, scopes: [
        "is:master",
        "read:trafficTickets:all"
      ], every: false })) {
        setClientSelectedOption({
          dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client),
          label: currentUser.client.type === "LEG" ? (`${currentUser.client.legal.socialReason} | CNPJ: ${maskCNPJ(currentUser.client.legal.cnpj)}`) : (`${currentUser.client.personal.name} | CPF: ${maskCPF(currentUser.client.personal.cpf)}`),
          value: currentUser.client.id
        });
        setTrafficTicket(prevState => ({
          ...prevState,
          client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client)
        }));
      }
    }, 1000);
  }, []); /* eslint-disable-line */

  const [activeKey, setActiveKey] = useState(ACTIVE_KEY_GENERAL_DATA);

  const [activeOptions] = useState([
    { value: true, label: t("Active") },
    { value: false, label: t("Inactive") }
  ]);

  const [gravityOptions] = useState([
    { value: "L", label: t("Title.Gravity.L") },
    { value: "M", label: t("Title.Gravity.M") },
    { value: "S", label: t("Title.Gravity.S") },
    { value: "V", label: t("Title.Gravity.V") }
  ]);

  const [clientSelectedOption, setClientSelectedOption] = useState(trafficTicket.client.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), trafficTicket.client),
    label: trafficTicket.client.type === "LEG" ? (`${trafficTicket.client.legal.socialReason} | CNPJ: ${maskCNPJ(trafficTicket.client.legal.cnpj)}`) : (`${trafficTicket.client.personal.name} | CPF: ${maskCPF(trafficTicket.client.personal.cpf)}`),
    value: trafficTicket.client.id
  });

  const [driverSelectedOption, setDriverSelectedOption] = useState(trafficTicket.driver.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(DriverSchemaReduced)), trafficTicket.driver),
    label: trafficTicket.driver.name,
    value: trafficTicket.driver.id
  });

  const [patrimonySelectedOption, setPatrimonySelectedOption] = useState(trafficTicket.patrimony.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(PatrimonySchemaReduced)), trafficTicket.patrimony),
    label: trafficTicket.patrimony.description,
    value: trafficTicket.patrimony.id
  });

  const [running, setRunning] = useState(false);

  const buttonDisabled = () => {
    if (!running) return false;
    return true;
  };

  const clientLoadOptions = (e, c) => {
    clientLoadOptionsDebounce(e, c);
  };

  const clientLoadOptionsDebounce = debounce(async (e, c) => {
    const clients = await clientService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterClient(clients));
  }, 1000);

  const changeActiveKey = (e) => {
    if (activeKey === e) return setActiveKey("-1");
    setActiveKey(e);
  };

  const driverLoadOptions = (e, c) => {
    driverLoadOptionsDebounce(e, c);
  };

  const driverLoadOptionsDebounce = debounce(async (e, c) => {
    const drivers = await driverService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterDriver(drivers));
  }, 1000);

  const filterClient = (clients) => {
    return clients.map(e => {
      return {
        dataAux: e,
        label: e.type === "LEG" ? (`${e.legal.socialReason} | CNPJ: ${maskCNPJ(e.legal.cnpj)}`) : `${e.personal.name} | CPF: ${maskCPF(e.personal.cpf)}`,
        value: e.id
      };
    });
  };

  const filterDriver = (drivers) => {
    return drivers.map(e => {
      return {
        dataAux: e,
        label: e.name,
        value: e.id
      };
    });
  };

  const filterPatrimony = (patrimonies) => {
    return patrimonies.map(e => {
      return {
        dataAux: e,
        label: e.description,
        value: e.id
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!running) {
      setRunning(true);
      let { eventKey, message, valid } = isValidForm(e);
      if (!valid) {
        if (!message) {
          message = t("Error.Fields.Empty");
        }
        if (eventKey !== -1) {
          setActiveKey(eventKey);
        }
        addNotice({
          title: t("Title.TrafficTicket"),
          content: message
        });
      }
      else {
        let response = false;
        if (isCreate()) {
          response = await trafficTicketService.create({
            data: handleSubmitForm({ trafficTicket }),
            token: currentUser.token
          });
        }
        else {
          response = await trafficTicketService.update({
            id: trafficTicket.id,
            data: handleSubmitForm({ trafficTicket }),
            token: currentUser.token
          });
        }

        if (response.success) {
          addNotice({
            title: t("Title.TrafficTicket"),
            content: isCreate() ? t("Success.Insert") : t("Success.Update")
          });
          setTimeout(() => {
            __close();
          }, 200);
        }
        else {
          addNotice({
            title: t("Title.TrafficTicket"),
            content: t(`Error.TrafficTicket.${response.error.type}.${ucFirstAll(response.error.details.path)}`)
          });
        }
      }
    }
    setRunning(false);
  };

  const handleSubmitForm = ({ trafficTicket }) => {
    let newTrafficTicket = Object.assign({}, JSON.parse(JSON.stringify(trafficTicket)));
    newTrafficTicket = clearObject({ data: newTrafficTicket });
    newTrafficTicket.id = undefined;
    return formToJson(newTrafficTicket);
  };

  const inputDisabled = () => {
    if (!running) return false;
    return true;
  };

  const isCreate = () => {
    return trafficTicket.id === "";
  };

  const patrimonyLoadOptions = (e, c) => {
    patrimonyLoadOptionsDebounce(e, c);
  };

  const patrimonyLoadOptionsDebounce = debounce(async (e, c) => {
    const patrimonies = await patrimonyService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterPatrimony(patrimonies));
  }, 1000);

  return (
    <Form id="forms-traffic-ticket" noValidate onSubmit={handleSubmit}>
      <Accordion defaultActiveKey={ACTIVE_KEY_GENERAL_DATA} activeKey={activeKey}>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_GENERAL_DATA} onClick={e => changeActiveKey(ACTIVE_KEY_GENERAL_DATA)}>{t("Title.GeneralData")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_GENERAL_DATA}>
            <Card.Body>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-active">
                    <Form.Label>{t("Label.Active")}:</Form.Label>
                    <Select
                      className="menu-outer-bottom"
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      isDisabled={inputDisabled()}
                      onChange={e => {
                        const active = e.value;
                        setTrafficTicket(prevState => ({
                          ...prevState,
                          active
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      options={activeOptions}
                      value={activeOptions.find(x => x.value === trafficTicket.active)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-client">
                    <Form.Label>{t("Title.Client")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-bottom ${!isEmpty(trafficTicket.client.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled() || !isScopes({
                        currentUser,
                        scopes: [
                          "is:master",
                          "read:trafficTockets:all"
                        ],
                        every: false
                      })}
                      loadOptions={clientLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if(e === null) {
                          setClientSelectedOption(e);
                          setTrafficTicket(prevState => ({
                            ...prevState,
                            client: JSON.parse(JSON.stringify(ClientSchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setClientSelectedOption({ dataAux, label, value });
                          setTrafficTicket(prevState => ({
                            ...prevState,
                            client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      placeholder={t("Title.Client.Select")}
                      value={clientSelectedOption}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(trafficTicket.client.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Client")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-driver">
                    <Form.Label>{t("Title.Driver")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-bottom ${!isEmpty(trafficTicket.driver.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled()}
                      loadOptions={driverLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if (e === null) {
                          setDriverSelectedOption(e);
                          setTrafficTicket(prevState => ({
                            ...prevState,
                            driver: JSON.parse(JSON.stringify(DriverSchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setDriverSelectedOption({ dataAux, label, value });
                          setTrafficTicket(prevState => ({
                            ...prevState,
                            driver: mergeObject(JSON.parse(JSON.stringify(DriverSchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      placeholder={t("Title.Driver.Select")}
                      value={driverSelectedOption}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(trafficTicket.driver.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Driver")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-patrimony">
                    <Form.Label>{t("Title.Patrimony")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-bottom ${!isEmpty(trafficTicket.patrimony.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled()}
                      loadOptions={patrimonyLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if(e === null) {
                          setPatrimonySelectedOption(e);
                          setTrafficTicket(prevState => ({
                            ...prevState,
                            patrimony: JSON.parse(JSON.stringify(PatrimonySchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setPatrimonySelectedOption({ dataAux, label, value });
                          setTrafficTicket(prevState => ({
                            ...prevState,
                            patrimony: mergeObject(JSON.parse(JSON.stringify(PatrimonySchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      placeholder={t("Title.Patrimony.Select")}
                      value={patrimonySelectedOption}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(trafficTicket.patrimony.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Patrimony")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_DETAIL_DATA} onClick={e => changeActiveKey(ACTIVE_KEY_DETAIL_DATA)}>{t("Title.DetailData")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_DETAIL_DATA}>
            <Card.Body>
              <Row>
                <Col xs={12}>
                  <div className="filter-date-fields">
                    <Form.Group controlId="forms-traffic-ticket-start-at">
                      <Form.Label>{t("Title.Date")}:</Form.Label>
                      <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                          <InputGroup.Text><i className="fas fa-calendar-minus" /></InputGroup.Text>
                        </InputGroup.Prepend>
                        <DatePicker
                          disabled={inputDisabled()}
                          selected={DateUtil.toDate({ date: trafficTicket.date })}
                          onChange={dateStart => {
                            const todayDate = `${(new Date()).getFullYear()}-${(new Date()).getMonth()+1}-${(new Date()).getDate()}`;
                            const date = dateStart === null ? new Date(`${todayDate} 00:00:00`) : dateStart;
                            setTrafficTicket(prevState => ({
                              ...prevState,
                              date
                            }));
                          }}
                          selectsStart
                          startDate={DateUtil.toDate({ date: trafficTicket.date })}
                          timeFormat="HH:mm"
                          timeIntervals={5}
                          showTimeSelect
                          showMonthDropdown
                          showYearDropdown
                          dateFormat="dd/MM/yyyy HH:mm:ss"
                          className="react-datepicker-form-control"
                          id="forms-traffic-ticket-start-at"
                          popperPlacement="top-center"
                          popperModifiers={{
                            offset: {
                              enabled: true,
                              offset: "5px, 10px"
                            },
                            preventOverflow: {
                              enabled: true,
                              escapeWithReference: false,
                              boundariesElement: "viewport"
                            }
                          }}
                          locale="pt-BR"
                          dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                        />
                      </InputGroup>
                    </Form.Group>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-gravity">
                    <Form.Label>{t("Label.Gravity")}:</Form.Label>
                    <Select
                      className="menu-outer-bottom"
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      isDisabled={inputDisabled()}
                      onChange={e => {
                        const gravity = e.value;
                        setTrafficTicket(prevState => ({
                          ...prevState,
                          gravity
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      options={gravityOptions}
                      value={gravityOptions.find(x => x.value === trafficTicket.gravity)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-code">
                    <Form.Label>{t("Label.Code")}:</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t("Label.Code")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const code = e.target.value;
                        setTrafficTicket(prevState => ({
                          ...prevState,
                          code
                        }));
                      }}
                      autoComplete="off"
                      value={trafficTicket.code}
                      isValid={!isEmpty(trafficTicket.code)}
                      isInvalid={isEmpty(trafficTicket.code)}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Code")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-price">
                    <Form.Label>{t("Label.Price")}:</Form.Label>
                    <CurrencyInput
                      id="forms-traffic-ticket-price"
                      className={`form-control ${!isEmpty(trafficTicket.price) ? "is-valid" : "is-invalid"}`}
                      type="text"
                      placeholder={t("Placeholder.Price")}
                      disabled={inputDisabled()}
                      decimalSeparator=","
                      thousandSeparator="."
                      precision="2"
                      prefix="R$ "
                      value={trafficTicket.price}
                      onChangeEvent={e => {
                        const price = e.target.value;
                        setTrafficTicket(prevState => ({
                          ...prevState,
                          price
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Price")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-speed">
                    <Form.Label>{t("Label.SpeedCalendar")}:</Form.Label>
                    <CurrencyInput
                      id="forms-traffic-ticket-speed"
                      className={"form-control"}
                      type="text"
                      placeholder={t("Placeholder.SpeedCalendar")}
                      disabled={inputDisabled()}
                      decimalSeparator="."
                      thousandSeparator=""
                      precision="0"
                      prefix=""
                      value={trafficTicket.speed}
                      onChangeEvent={e => {
                        const speed = e.target.value;
                        setTrafficTicket(prevState => ({
                          ...prevState,
                          speed
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.SpeedCalendar")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-address">
                    <Form.Label>{t("Label.Address")}:</Form.Label>
                    <Form.Control
                      as="textarea"
                      placeholder={t("Label.Address")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const address = e.target.value;
                        setTrafficTicket(prevState => ({
                          ...prevState,
                          address
                        }));
                      }}
                      autoComplete="off"
                      value={trafficTicket.address}
                      isValid={!isEmpty(trafficTicket.address)}
                      isInvalid={isEmpty(trafficTicket.address)}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Address")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-traffic-ticket-description">
                    <Form.Label>{t("Label.Description")}:</Form.Label>
                    <Form.Control
                      as="textarea"
                      placeholder={t("Label.Description")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const description = e.target.value;
                        setTrafficTicket(prevState => ({
                          ...prevState,
                          description
                        }));
                      }}
                      autoComplete="off"
                      value={trafficTicket.description}
                      isValid={!isEmpty(trafficTicket.description)}
                      isInvalid={isEmpty(trafficTicket.description)}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Description")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      <Form.Group className="default-form-button">
        <Button
          variant="dark"
          type="submit"
          disabled={buttonDisabled()}
        >
          {running ? <Spinner animation="border" size="sm" /> : t("Button.Save")}
        </Button>
      </Form.Group>
    </Form>
  );
};

const mapStateToProps = state => ({
  props: {
    currentUser: state.users.currentUser
  }
});

const mapDispatchToProps = dispatch => ({
  funcs: bindActionCreators(Object.assign({}, NotificationsActions), dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(TrafficTicket));
